import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import AuditService from "../api/AuditService";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import AuditLogFilterForm from "./AuditLogFilterForm";
import { useNavigate } from "react-router-dom";

const formatDateTime = (ts) => ts ? dayjs(ts).format('YYYY-MM-DD HH:mm:ss') : "";

const AuditLogPage = () => {
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const isLoading = useSelector((state) => state.user.isLoading);
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);

  const [filter, setFilter] = useState(() => {
    const now = new Date();
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    const pad = (n) => String(n).padStart(2, "0");
    const formatDate = (d) => {
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59`;
    };
    return {
      userId: "",
      status: "",
      from: sevenDaysAgo.toISOString().slice(0, 16),
      to: formatDate(now)
    };
  });

  useEffect(() => {
    if (!isLoading && isLoggedIn) {
      fetchFilteredLogs();
    }
    if (!isLoading && !isLoggedIn) {
      navigate("/login");
    }
  }, [isLoading, isLoggedIn, page, rowsPerPage]);

  if (!isLoading && !isLoggedIn) {
    return <div>Loading...</div>;
  }

  const parseWithOffset = (dt) => dt ? new Date(dt).toISOString() : null;

  const fetchFilteredLogs = async () => {
    try {
      setLoading(true);
      const preparedFilter = {};
      if (filter.userId) preparedFilter.userId = Number(filter.userId);
      if (filter.status) preparedFilter.status = filter.status;
      if (filter.from) preparedFilter.from = parseWithOffset(filter.from);
      if (filter.to) preparedFilter.to = parseWithOffset(filter.to);
      const response = await AuditService.postFilteredLogs(preparedFilter, page, rowsPerPage);
      const data = response.data;
      setLogs(data.content || []);
      setTotalElements(data.totalElements || 0);
    } catch (err) {
      setError("Не вдалося завантажити фільтровані логи.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Аудит логи</Typography>

      <AuditLogFilterForm
        filter={filter}
        setFilter={setFilter}
        onApply={() => {
          setPage(0);
          fetchFilteredLogs();
        }}
      />

      {error && (
        <div className="container alert alert-warning text-center mt-3">
          {error}
        </div>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress/>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxWidth: '95%' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell>{log.id}</TableCell>
                    <TableCell>{log.userId}</TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>{log.resource || "—"}</TableCell>
                    <TableCell style={{ whiteSpace: 'pre-wrap', maxWidth: 200 }}>
                      {log.details}
                    </TableCell>
                    <TableCell>{formatDateTime(log.timestamp)}</TableCell>
                    <TableCell>{log.ipAddress || "—"}</TableCell>
                    <TableCell>{log.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </>
      )}
    </Box>
  );
};

export default AuditLogPage;
